import api from "@/lib/axiosConfig";
import { Collector } from "@/lib/types";
import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { RelativePosition } from "../dtos";

export const baseRoute = "back/api/collectors";

interface CollectorDTO {
  where?: any;
  order?: any;
  take?: number;
  skip?: number;
}

export async function getCollectors({
  where,
  order,
  take,
  skip,
}: CollectorDTO): Promise<Collector[]> {
  const { data } = await api.post(
    `back/api/collectors`,
    {
      where,
      orderBy: order,
    },
    { params: { take, skip } },
  );
  return data;
}

export const useGetAllCollectors = ({
  where,
  order,
  take,
  skip,
}: CollectorDTO) => {
  return useQuery({
    queryKey: ["collectors", where, order, take, skip],
    queryFn: () => getCollectors({ where, order, take, skip }),
    placeholderData: keepPreviousData,
  });
};

export async function getCountByStatus(where?: object) {
  const { data } = await api.post(`back/api/collectors/countByStatus`, {
    where,
  });
  return data;
}

export const useCountByStatus = () => {
  return useQuery({
    queryKey: ["countByStatus", "collector"],
    queryFn: () => getCountByStatus(),
  });
};

export const getRelativePosition = async (
  id: string,
  order: object,
  where: object,
) => {
  const { data } = await api.post<RelativePosition>(
    `${baseRoute}/${id}/position`,
    {
      order,
      where,
    },
  );
  return data;
};

const getCollector = async (id: string) => {
  const { data } = await api.get<Collector>(`back/api/collectors/${id}`);
  return data;
};

export const useGetCollector = (id?: string) => {
  return useQuery({
    queryKey: ["collector", id],
    queryFn: () => (id ? getCollector(id) : undefined),
  });
};

async function updateCollector(collector: Collector) {
  const {
    instanceId: _iid,
    updatedById: _ubid,
    createdById: _cbid,
    ...rest
  } = collector;
  return await api.patch(`back/api/collectors/${collector.id}`, {
    data: rest,
  });
}

export function useUpdateCollector() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateCollector,
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: ["collector", v.id] });
      queryClient.invalidateQueries({ queryKey: ["collectors"] });
    },
  });
}

type CreateCollectorParams = {
  collectionneursDescriptionPrenom: string;
  collectionneursDescriptionNom: string;
};
type CreateCollectorResponse = Pick<Collector, "id">;

const createCollector = async ({
  collectionneursDescriptionPrenom,
  collectionneursDescriptionNom,
}: CreateCollectorParams) => {
  return await api
    .post<CreateCollectorResponse>("back/api/collectors/create", {
      collectionneursDescriptionPrenom,
      collectionneursDescriptionNom,
    })
    .then((res) => res.data);
};

export const useCreateCollector = (
  options?: UseMutationOptions<
    CreateCollectorResponse,
    Error,
    CreateCollectorParams
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: createCollector,
    onSuccess: (data, vars, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["collectors"] });
      queryClient.invalidateQueries({ queryKey: ["collector", data.id] });
      queryClient.invalidateQueries({
        queryKey: ["countByStatus", "collector"],
      });
      queryClient.invalidateQueries({ queryKey: ["instance", "count-data"] });
      options?.onSuccess?.(data, vars, ctx);
    },
  });
};
